import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { TrainingCardProps } from '@/types/trainings';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format, differenceInDays } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { TrainingNotesModal } from './TrainingNotesModal';
import { useI18n } from '@/contexts/I18nContext';
import { formatDateTimeForDisplay } from '@/utils/timezoneUtils';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Scheduled':
      return 'bg-white/10 text-white border-white/20';
    case 'Completed':
      return 'bg-green-600 text-white border-green-600';
    case 'Missed':
      return 'bg-transparent text-white border-white';
    default:
      return 'bg-white/10 text-white border-white/20';
  }
};

const getCardBackgroundColor = (status: string) => {
  switch (status) {
    case 'Scheduled':
      return 'bg-[#524545]';
    case 'Completed':
      return 'bg-[#475245]';
    case 'Missed':
      return 'bg-[#a94442]'; // vermelho escuro para {t('calendar.missed')}
    default:
      return 'bg-[#524545]';
  }
};

const formatTime = (dateString: string) => {
  return formatDateTimeForDisplay(dateString).time;
};

const isPastTraining = (endTime: string) => {
  return new Date(endTime) < new Date();
};

// Check if training is recent enough to show notes button (within 30 days)
const isRecentTraining = (startTime: string) => {
  const trainingDate = new Date(startTime);
  const today = new Date();
  const daysDiff = differenceInDays(today, trainingDate);
  return daysDiff <= 30;
};

export interface TrainingCardRef {
  scrollIntoView: () => void;
  animateUpdate: () => void;
}

interface TrainingCardPropsWithNotes extends TrainingCardProps {
  notesData?: any;
  notesRefreshKey?: number;
  isUpdatingNotes?: boolean;
}

export const TrainingCard = forwardRef<TrainingCardRef, TrainingCardPropsWithNotes>(({ training, onStatusUpdate, onNotesUpdate, isCurrentDay, onViewTraining, notesData, notesRefreshKey, isUpdatingNotes }, ref) => {
  const { t } = useI18n();
  // Removed isNotesModalOpen - using global modal from Calendar.tsx
  const [showNotesAfterStatusUpdate, setShowNotesAfterStatusUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateAnimation, setUpdateAnimation] = useState(false);
  
  // Get localized training type
  const getLocalizedTrainingType = (type: string) => {
    switch (type) {
      case 'Group Class':
        return t('calendar.group_class');
      case 'Private Class':
        return t('calendar.private_class');
      default:
        return type;
    }
  };

  // Get training notes data from props
  const trainingNotesData = notesData?.[training.id];
  
  // Debug removed - hasNotes logic fixed
  
  const hasNotes = !!(
    trainingNotesData?.notes || // If notes record exists (even with empty fields)
    (trainingNotesData?.techniques && trainingNotesData.techniques.length > 0) ||
    (trainingNotesData?.partners && trainingNotesData.partners.length > 0)
  );

  
  const showActionButtons = training.status === 'Scheduled' && isPastTraining(training.end_time);
  const showNotesButton = (
    training.status === 'Completed' && 
    (isRecentTraining(training.start_time) || showNotesAfterStatusUpdate)
  );


  // Force re-render when notesRefreshKey changes to update notes status
  useEffect(() => {
    // This effect will trigger a re-render when notesRefreshKey changes
    // The hasNotes calculation will be re-evaluated with fresh data
  }, [notesRefreshKey, trainingNotesData, hasNotes]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    scrollIntoView: () => {
      const element = document.getElementById(`training-card-${training.id}`);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    },
    animateUpdate: () => {
      setUpdateAnimation(true);
      setTimeout(() => setUpdateAnimation(false), 1000);
    }
  }));

  const handleStatusUpdate = async (status: 'Completed' | 'Missed') => {
    setIsUpdating(true);
    setUpdateAnimation(true);
    
    try {
      await onStatusUpdate(training.id, status);
      
      // If user clicked "Yes" (Completed), show notes button immediately
      if (status === 'Completed') {
        setShowNotesAfterStatusUpdate(true);
      }
      
      // Trigger animation
      setTimeout(() => setUpdateAnimation(false), 1000);
    } catch (error) {
      console.error('Error updating training status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNotesClick = () => {
    // Call onNotesUpdate to trigger parent component to open the global modal
    onNotesUpdate(training.id, '');
  };

  const handleNotesSave = (trainingId: string, notes: string) => {
    onNotesUpdate(trainingId, notes);
    // Reset the flag after saving notes
    setShowNotesAfterStatusUpdate(false);
    // This function is kept for backward compatibility but the new modal handles saving internally
  };

  const handleDataSaved = () => {
    // Call onNotesUpdate to trigger parent component data refresh
    onNotesUpdate(training.id, '');
  };

  return (
    <>
      <div 
        id={`training-card-${training.id}`}
        onClick={() => onViewTraining?.(training)}
        className={`p-3 rounded-2xl ${getCardBackgroundColor(training.status)} mb-3 relative transition-all duration-500 ease-out cursor-pointer hover:scale-[1.02] ${
          updateAnimation 
            ? 'scale-105 shadow-lg shadow-green-500/20 ring-2 ring-green-500/50' 
            : 'scale-100'
        } ${isUpdating ? 'opacity-75' : 'opacity-100'}`}
      >
        {/* External link icon */}
        <div className="absolute top-3 right-3">
          <ExternalLink className="w-4 h-4 text-white/60" />
        </div>

        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 pr-8">
            <div className="flex items-baseline gap-2 mb-2">
              <h3 className="font-bold text-white text-sm font-roboto">
                {training.title || getLocalizedTrainingType(training.type)}
              </h3>
              {training.modality && (
                <span className="text-white/50 text-[11px] font-medium font-roboto">
                  {training.modality}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-white/60 mb-2 font-roboto font-normal">
              <span>{t('calendar.from_to', { start: formatTime(training.start_time), end: formatTime(training.end_time) })}</span>
            </div>
            {/* Descrição removida */}
          </div>
        </div>

        {/* Status Badge and Notes Button */}
        <div className="flex items-center justify-between mb-3">
        <Badge 
          variant="outline" 
            className={`${getStatusColor(training.status)} text-[10.5px] font-medium px-1.5 py-0.5 rounded-full font-roboto transition-all duration-300 ${
              updateAnimation ? 'scale-110' : 'scale-100'
            }`}
        >
            {training.status}
        </Badge>
          
          {/* Notes Button - Show for recent {t('calendar.completed')}/{t('calendar.missed')} trainings or after status update */}
          {showNotesButton && (
            <>
              {trainingNotesData === null ? (
                // Shimmer loading
                <div className="animate-pulse">
                  <div className="h-4 w-20 bg-white/20 rounded"></div>
                </div>
              ) : (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNotesClick();
                  }}
                  disabled={isUpdatingNotes}
                  style={{
                    opacity: isUpdatingNotes ? 0.6 : 1,
                    cursor: isUpdatingNotes ? 'not-allowed' : 'pointer'
                  }}
                  className="text-white/60 hover:text-white underline font-roboto text-[10.5px] font-semibold transition-all duration-300 hover:scale-105"
                >
                  {hasNotes ? t('calendar.review_notes') : t('calendar.add_notes')}
                </button>
              )}
            </>
          )}
      </div>

        {/* Action Buttons */}
      {showActionButtons && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-white/80 font-roboto text-xs font-bold">{t('calendar.did_you_go')}</span>
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusUpdate('Completed');
                  }}
                  disabled={isUpdating}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full font-roboto text-xs font-bold px-4 py-0.5 h-6 leading-none transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('calendar.yes')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusUpdate('Missed');
                  }}
                  disabled={isUpdating}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-full font-roboto text-xs font-bold px-4 py-0.5 h-6 leading-none transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('calendar.no')}
                </button>
              </div>
            </div>
        </div>
      )}
    </div>

      {/* Training Notes Modal - REMOVED: This is handled by the global modal in Calendar.tsx */}
    </>
  );
});

TrainingCard.displayName = 'TrainingCard'; 